import React from 'react';
import { useEditor, EditorContent, Node } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { Button } from './ui/button';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Undo, Redo, Code, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Settings2 } from 'lucide-react';
import { showNotification } from '@/lib/generalNotifications';
import { showEmailSentConfirmation } from '@/lib/emailConfirmations';

// Utility function to clean HTML content
const cleanHtmlContent = (content: string): string => {
  if (!content) return '';
  
  try {
    // Handle escaped HTML entities
    const cleanedContent = content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
    
    // Fix any broken HTML by parsing it through a DOM element
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleanedContent, 'text/html');
    
    // Get the sanitized HTML
    return doc.body.innerHTML;
  } catch (error) {
    console.error('Error cleaning HTML content:', error);
    return content;
  }
};

// Helper function to format dates safely (timezone-safe)
const formatDate = (date: Date): string => {
  // Get local date components without timezone conversion
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Create a new date using local components to avoid timezone shift
  const localDate = new Date(year, month, day);
  
  return localDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const TEMPLATE_VARIABLES = [
  { label: 'First Name', value: '{{firstName}}' },
  { label: 'Customer Name', value: '{{customerName}}' },
  { label: 'Invoice Number', value: '{{invoiceNumber}}' },
  { label: 'Invoice Amount', value: '{{invoiceAmount}}' },
  { label: 'Due Date', value: '{{dueDate}}' },
  { label: 'Payment Date', value: '{{paymentDate}}' },
  { label: 'Refund Amount', value: '{{refundAmount}}' },
  { label: 'Refund Date', value: '{{refundDate}}' },
  { label: 'Failed Date', value: '{{failedDate}}' },
  { label: 'Failure Reason', value: '{{failureReason}}' },
  { label: 'Company Name', value: '{{companyName}}' },
  { label: 'Payment Link', value: '{{paymentLink}}' },
  { label: 'Frequency', value: '{{frequency}}' },
];

// Add button settings interface
interface ButtonSettings {
  backgroundColor: string;
  padding: string;
  width: string;
  buttonText: string;
}

const defaultButtonSettings: ButtonSettings = {
  backgroundColor: '#4ADE80',
  padding: '12px 32px',
  width: '240px',
  buttonText: 'View & Pay Invoice'
};

const MenuBar = ({ 
  editor, 
  buttonSettings, 
  setButtonSettings
}: { 
  editor: any; 
  buttonSettings: ButtonSettings;
  setButtonSettings: React.Dispatch<React.SetStateAction<ButtonSettings>>;
}) => {
  const [showHtmlDialog, setShowHtmlDialog] = React.useState(false);
  const [htmlContent, setHtmlContent] = React.useState('');

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const insertVariable = (value: string) => {
    if (value) {
      editor.chain().focus().insertContent(value).run();
    }
  };

  const insertPaymentButton = () => {
    // Remove any existing payment button placeholder that might exist as plain text
    editor.chain().focus().deleteSelection().run();
    
    // Insert the payment button node with current settings
    editor.chain().focus().insertContent({
      type: 'paymentButton',
      attrs: buttonSettings
    }).run();
    
    // Store the current button settings in the editor state
    // This ensures the settings are preserved when the editor is rendered
    editor.storage.paymentButton = { 
      ...buttonSettings 
    };
  };

  const handleEditHtml = () => {
    setHtmlContent(editor.getHTML());
    setShowHtmlDialog(true);
  };

  const handleSaveHtml = () => {
    try {
      // Clean and parse the HTML content
      const cleanedHtml = cleanHtmlContent(htmlContent);
      
      // Set the content using the cleaned HTML
      editor.commands.setContent(cleanedHtml);
      setShowHtmlDialog(false);
    } catch (error) {
      console.error('Error parsing HTML:', error);
      // Use the content directly if parsing fails
      editor.commands.setContent(htmlContent);
      setShowHtmlDialog(false);
    }
  };

  return (
    <>
      <div className="border-b p-2 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scroll-smooth">
        <div className="flex gap-2 items-center min-w-max">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'bg-muted' : ''}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'bg-muted' : ''}
          >
            <Italic className="h-4 w-4" />
          </Button>

          <div className="h-4 w-px bg-border mx-2" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? 'bg-muted' : ''}
          >
            <AlignLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? 'bg-muted' : ''}
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? 'bg-muted' : ''}
          >
            <AlignRight className="h-4 w-4" />
          </Button>

          <div className="h-4 w-px bg-border mx-2" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'bg-muted' : ''}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'bg-muted' : ''}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={addLink}
            className={editor.isActive('link') ? 'bg-muted' : ''}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
          >
            <Redo className="h-4 w-4" />
          </Button>

          <div className="h-4 w-px bg-border mx-2" />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleEditHtml}
          >
            <Code className="h-4 w-4" />
          </Button>

          <div className="h-4 w-px bg-border mx-2" />

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex gap-2 items-center"
              >
                <Settings2 className="h-4 w-4" />
                Button Settings
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <div className="p-3 border rounded bg-gray-50">
                  <Label className="block mb-2">Button Preview</Label>
                  <div className="text-center">
                    <div 
                      style={{
                        display: 'inline-block',
                        backgroundColor: buttonSettings.backgroundColor,
                        color: 'white',
                        padding: buttonSettings.padding,
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '16px',
                        border: 'none',
                        textAlign: 'center',
                        minWidth: buttonSettings.width,
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {buttonSettings.buttonText}
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label>Button Text</Label>
                  <Input
                    type="text"
                    value={buttonSettings.buttonText}
                    onChange={(e) => setButtonSettings(prev => ({
                      ...prev,
                      buttonText: e.target.value
                    }))}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Background Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={buttonSettings.backgroundColor}
                      onChange={(e) => setButtonSettings(prev => ({
                        ...prev,
                        backgroundColor: e.target.value
                      }))}
                      className="w-[100px] h-[38px] p-1"
                    />
                    <Input
                      type="text"
                      value={buttonSettings.backgroundColor}
                      onChange={(e) => setButtonSettings(prev => ({
                        ...prev,
                        backgroundColor: e.target.value
                      }))}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Padding</Label>
                  <Select
                    value={buttonSettings.padding}
                    onValueChange={(value) => setButtonSettings(prev => ({
                      ...prev,
                      padding: value
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select padding" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8px 24px">Small</SelectItem>
                      <SelectItem value="12px 32px">Medium</SelectItem>
                      <SelectItem value="16px 48px">Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Width</Label>
                  <Select
                    value={buttonSettings.width}
                    onValueChange={(value) => setButtonSettings(prev => ({
                      ...prev,
                      width: value
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select width" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="200px">Small (200px)</SelectItem>
                      <SelectItem value="240px">Medium (240px)</SelectItem>
                      <SelectItem value="300px">Large (300px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="sm"
            onClick={insertPaymentButton}
          >
            Insert Payment Button
          </Button>

          <div className="h-4 w-px bg-border mx-2" />

          <Select onValueChange={insertVariable}>
            <SelectTrigger className="h-8 w-[200px] text-sm">
              <SelectValue placeholder="Insert variable..." />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATE_VARIABLES.map(({ label, value }) => (
                <SelectItem key={value} value={value} className="text-sm">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Dialog open={showHtmlDialog} onOpenChange={setShowHtmlDialog}>
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Edit HTML Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              className="font-mono text-sm min-h-[400px]"
              placeholder="Enter HTML code..."
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowHtmlDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveHtml}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Update PaymentButton Node to accept settings and handle both formats
const PaymentButton = Node.create({
  name: 'paymentButton',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      backgroundColor: {
        default: defaultButtonSettings.backgroundColor
      },
      padding: {
        default: defaultButtonSettings.padding
      },
      width: {
        default: defaultButtonSettings.width
      },
      buttonText: {
        default: defaultButtonSettings.buttonText
      }
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="payment-button"]',
      },
      // Also recognize the raw variable format from templates
      {
        tag: 'p',
        getAttrs: (node) => {
          const el = node as HTMLElement;
          return el.textContent?.includes('{{paymentLink}}') ? {} : false;
        }
      }
    ]
  },

  renderHTML({ node }) {
    const attrs = node.attrs;
    
    // Create a visual representation of the payment button in the editor
    // This ensures the button appears correctly styled in the WYSIWYG view
    return ['div', { 
      'data-type': 'payment-button', 
      class: 'payment-button-wrapper',
      style: 'text-align: center; margin: 30px 0;'
    }, 
      ['a', { 
        href: '#payment-link',
        class: 'payment-button', 
        style: `
          display: inline-block;
          background-color: ${attrs.backgroundColor};
          color: white;
          padding: ${attrs.padding};
          text-decoration: none;
          border-radius: 6px;
          font-weight: 500;
          font-size: 18px;
          border: none;
          text-align: center;
          min-width: ${attrs.width};
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        `
      }, attrs.buttonText || 'View & Pay Invoice'],
      // Hidden actual variable that will be replaced during email sending
      ['span', { style: 'display: none;' }, '{{paymentLink}}']
    ]
  },
});

// Update button styles to use CSS variables for dynamic values
const buttonStyles = `
  .payment-button-wrapper {
    cursor: default;
    user-select: none;
    margin: 30px 0;
    text-align: center;
  }
  .payment-button {
    display: inline-block !important;
    color: white !important;
    text-decoration: none !important;
    border-radius: 6px !important;
    font-weight: 500 !important;
    font-size: 18px !important;
    border: none !important;
    text-align: center !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  }
  
  /* Preview mode styles */
  .email-preview-container {
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    overflow: hidden;
  }
  .email-preview-container .email-header {
    background-color: #f8fafc;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e2e8f0;
  }
  .email-preview-container .email-body {
    padding: 1.5rem;
    background-color: white;
  }
  .email-preview-container .email-body p {
    margin-bottom: 0.75rem;
  }
  .email-preview-container .email-body ul, 
  .email-preview-container .email-body ol {
    padding-left: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

interface EmailEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  previewContent?: React.ReactNode;
  companyId: string;
  subject: string;
  onSubjectChange: (subject: string) => void;
  paymentUrl: string;
}

export function EmailEditor({ content, onChange, placeholder, previewContent, companyId, subject, onSubjectChange, paymentUrl }: EmailEditorProps) {
  const [mode, setMode] = React.useState<'edit' | 'preview'>('edit');
  const [testEmailOpen, setTestEmailOpen] = React.useState(false);
  const [testEmailAddress, setTestEmailAddress] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const [buttonSettings, setButtonSettings] = React.useState<ButtonSettings>(defaultButtonSettings);
  const detectFullHtml = React.useCallback((html: string) => /<!doctype/i.test(html || '') || /<html[\s>]/i.test(html || '') || /<head[\s>]/i.test(html || '') || /<style[\s>]/i.test(html || ''), []);
  const [rawMode, setRawMode] = React.useState<boolean>(detectFullHtml(content));
  const [rawHtml, setRawHtml] = React.useState<string>(content);
  
  // Process the initial content if it contains raw HTML tags or payment links
  const initialContent = React.useMemo(() => {
    if (rawMode) {
      return '';
    }
    let processedContent = cleanHtmlContent(content);
    
    // Check for raw payment link variables and convert them to proper payment button nodes
    if (processedContent.includes('{{paymentLink}}')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = processedContent;
      
      // Find paragraphs containing the payment link placeholder
      const paragraphs = tempDiv.querySelectorAll('p');
      paragraphs.forEach(p => {
        if (p.textContent?.includes('{{paymentLink}}')) {
          // Replace with styled payment button
          const buttonHtml = `
            <div data-type="payment-button" class="payment-button-wrapper" style="text-align: center; margin: 30px 0;">
              <a href="${paymentUrl}" class="payment-button" style="
                display: inline-block;
                background-color: ${buttonSettings.backgroundColor};
                color: white;
                padding: ${buttonSettings.padding};
                text-decoration: none;
                border-radius: 6px;
                font-weight: 500;
                font-size: 18px;
                border: none;
                text-align: center;
                min-width: ${buttonSettings.width};
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
                ${buttonSettings.buttonText || 'View & Pay Invoice'}
              </a>
              <span style="display: none;">{{paymentLink}}</span>
            </div>
          `;
          
          // Only replace if it's just the payment link placeholder
          if (p.textContent.trim() === '{{paymentLink}}') {
            p.outerHTML = buttonHtml;
          }
        }
      });
      
      processedContent = tempDiv.innerHTML;
    }
    
    return processedContent;
  }, [content, buttonSettings, paymentUrl, rawMode]);
  
  // Get the current email content for preview
  const getPreviewContent = () => {
    if (rawMode) {
      return rawHtml || content;
    }
    if (!editor) return "";
    
    let previewHtml = editor.getHTML();
    
    // Clean any remaining raw HTML tags
    previewHtml = cleanHtmlContent(previewHtml);
    
    // Replace template variables with preview values
    const previewData = {
      firstName: "John",
      customerName: "John Smith",
      customerAddress: "123 Main Street, Anytown, US 12345",
      invoiceNumber: "INV-001",
      invoiceAmount: "$2,875.25",
      total: 2875.25,
      dueDate: "March 15, 2024",
      paymentDate: "March 1, 2024",
      refundAmount: "$500.00",
      refundDate: "March 2, 2024",
      failedDate: "March 1, 2024",
      failureReason: "Insufficient funds",
      paymentLink: "#payment-link",
      companyName: "Ocean Cruises LLC",
      frequency: "Monthly"
    };
    
    // Replace all template variables with their preview values
    Object.entries(previewData).forEach(([key, value]) => {
      // Skip paymentLink as it needs special handling
      if (key === 'paymentLink') return;
      
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      previewHtml = previewHtml.replace(regex, String(value));
    });
    
    // Process any payment links in the preview
    if (previewHtml.includes('{{paymentLink}}') || previewHtml.includes('data-type="payment-button"')) {
      // Get the latest button settings
      const currentSettings = editor.storage.paymentButton || buttonSettings;
      
      // Create a styled button for preview
      const paymentButton = `
        <div style="text-align: center; margin: 30px 0;">
          <a href="${paymentUrl}" 
            style="display: inline-block; background-color: ${currentSettings.backgroundColor}; color: white; padding: ${currentSettings.padding}; text-decoration: none; border-radius: 6px; font-weight: 500; font-size: 18px; border: none; text-align: center; min-width: ${currentSettings.width}; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
            ${currentSettings.buttonText || 'View & Pay Invoice'}
          </a>
        </div>
      `;
      
      // Replace both the hidden span and the variable itself
      previewHtml = previewHtml.replace(/<div[^>]*data-type="payment-button"[^>]*>.*?<\/div>/gs, paymentButton);
      previewHtml = previewHtml.replace(/\{\{paymentLink\}\}/g, paymentButton);
      
      // Also handle custom text format {{paymentLink:Custom Text}}
      const customButtonRegex = /\{\{paymentLink:(.*?)\}\}/g;
      let match;
      
      while ((match = customButtonRegex.exec(previewHtml)) !== null) {
        const customText = match[1].trim();
        
        // Create button with custom text
        const customPaymentButton = `
          <div style="text-align: center; margin: 30px 0;">
            <a href="${paymentUrl}" 
              style="display: inline-block; background-color: ${currentSettings.backgroundColor}; color: white; padding: ${currentSettings.padding}; text-decoration: none; border-radius: 6px; font-weight: 500; font-size: 18px; border: none; text-align: center; min-width: ${currentSettings.width}; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
              ${customText}
            </a>
          </div>
        `;
        
        // Replace this specific occurrence
        previewHtml = previewHtml.replace(match[0], customPaymentButton);
      }
    }
    
    return previewHtml;
  };
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextAlign.configure({
        types: ['paragraph', 'heading'],
        alignments: ['left', 'center', 'right'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer nofollow',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Write your email template...',
      }),
      PaymentButton.configure({
        HTMLAttributes: {
          class: 'payment-button-wrapper',
        },
      }),
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose max-w-none p-4 focus-within:outline-none [&_*]:caret-black [&_p]:min-h-[1.5em]'
      },
    },
    enablePasteRules: true,
    enableInputRules: true,
    injectCSS: false, // Prevents TipTap from injecting its own CSS
    autofocus: false,
  });

  // Clean up HTML content when initializing or handling content changes
  React.useEffect(() => {
    if (rawMode) {
      setRawHtml(content);
      return;
    }
    if (editor && content !== editor.getHTML()) {
      // If the content has changed externally, clean it and update the editor
      const cleanedContent = cleanHtmlContent(content);
      if (cleanedContent !== editor.getHTML()) {
        editor.commands.setContent(cleanedContent);
      }
    }
  }, [editor, content, rawMode]);

  // Add event listener to handle paste events
  React.useEffect(() => {
    if (rawMode) return; // Raw mode handles paste via textarea natively
    if (editor) {
      const handlePaste = (event: ClipboardEvent) => {
        const clipboardData = event.clipboardData;
        if (clipboardData && clipboardData.getData('text/html')) {
          const html = clipboardData.getData('text/html');
          if (detectFullHtml(html)) {
            setRawMode(true);
            setRawHtml(html);
            onChange(html);
            event.preventDefault();
            return;
          }
          const cleanedHTML = cleanHtmlContent(html);
          editor.commands.insertContent(cleanedHTML);
          event.preventDefault();
        }
      };

      const editorElement = document.querySelector('.ProseMirror');
      if (editorElement) {
        editorElement.addEventListener('paste', handlePaste);
        return () => {
          editorElement.removeEventListener('paste', handlePaste);
        };
      }
    }
  }, [editor, rawMode, detectFullHtml, onChange]);

  const sendTestEmail = async () => {
    try {
      setIsSending(true);
      
      // Fetch the company information from the database first
      const companyResponse = await fetch(`/api/companies/${companyId}`);
      let companyName = 'Your Company';
      
      if (companyResponse.ok) {
        const companyData = await companyResponse.json();
        if (companyData?.company?.name) {
          companyName = companyData.company.name;
        }
      } else {
        console.warn('Could not fetch company information for test email, using default name');
      }
      
      // Create dummy data for a test invoice
      const testInvoice = {
        id: 'test-invoice-id',
        invoiceNumber: 'TEST-123',
        companyId: companyId,
        customerEmail: testEmailAddress,
        customerName: 'Test Customer',
        total: 99.99,
        dueDate: new Date().toISOString(),
        status: 'draft',
        paymentKey: 'test-payment-key-123' // Add a test payment key
      };
      
      // Create preview data for template variables
      const previewData = {
        customerName: 'Test Customer',
        invoiceNumber: 'TEST-123',
        invoiceAmount: '$99.99',
        dueDate: formatDate(new Date()),
        companyName: companyName, // Use the fetched company name
      };
      
      // Parse the subject to replace any template variables
      const parsedSubject = subject.replace(/\{\{(\w+)\}\}/g, function(match: string, key: string): string {
        const value = previewData[key as keyof typeof previewData];
        return value !== undefined ? String(value) : match;
      });

      // Process the email content
      const emailContent = rawMode ? (rawHtml || content) : getPreviewContent();

      // Ensure we're using the correct origin for the API request
      const apiUrl = new URL('/api/email', window.location.origin).toString();
      console.log('Making API request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoice: testInvoice,
          template: emailContent, // Use the processed content
          subject: parsedSubject
        }),
      });

      const responseText = await response.text();
      
      try {
        const data = JSON.parse(responseText);
        if (!response.ok) {
          throw new Error(data.error || 'Failed to send test email');
        }
        
        // Show small toast notification
        showNotification({
          title: 'Success',
          message: `Test email sent to ${testEmailAddress}`,
          type: 'success'
        });
        
        // Show the visual confirmation dialog
        showEmailSentConfirmation({
          invoiceNumber: 'TEST-123',
          recipientEmail: testEmailAddress,
          amount: 99.99
        });
        
        setTestEmailAddress('');
        setTestEmailOpen(false);
      } catch {
        console.error('Failed to parse API response:', responseText);
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send test email',
        type: 'error'
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="border rounded-md w-full">
      {!rawMode && <style>{buttonStyles}</style>}
      <div className="border-b p-3">
        <div className="mb-4">
          <Label htmlFor="subject">Email Subject</Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder="Enter email subject..."
            className="mt-1"
          />
        </div>
        <div className="flex items-center justify-between">
          <Tabs value={mode} onValueChange={(value) => setMode(value as 'edit' | 'preview')} className="w-[300px]">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="edit" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Editor</TabsTrigger>
              <TabsTrigger value="preview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Preview</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Button
              variant={rawMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setRawMode((v) => !v)}
              title="Toggle Full HTML Mode (paste complete HTML templates)"
            >
              {rawMode ? 'Full HTML Mode: On' : 'Full HTML Mode: Off'}
            </Button>
          </div>
        </div>
      </div>

      <div className="border-b">
        <MenuBar 
          editor={editor} 
          buttonSettings={buttonSettings} 
          setButtonSettings={setButtonSettings}
        />
      </div>

      <div className={cn(
        "w-full transition-opacity duration-200",
        mode === 'edit' ? 'opacity-100' : 'opacity-0 hidden'
      )}>
        {rawMode ? (
          <Textarea
            value={rawHtml}
            onChange={(e) => {
              setRawHtml(e.target.value);
              onChange(e.target.value);
            }}
            className="font-mono text-sm min-h-[400px]"
            placeholder="Paste full HTML (including <html> and <head>) here..."
          />
        ) : (
          <EditorContent 
            editor={editor} 
            className="prose max-w-none p-4 focus-within:outline-none [&_*]:caret-black [&_p]:min-h-[1.5em] [&_.ProseMirror-selectednode]:ring-0 [&_.ProseMirror]:min-h-[200px] [&_.ProseMirror]:outline-none [&_*]:selection:bg-blue-100"
          />
        )}
      </div>

      <div className={cn(
        "w-full transition-opacity duration-200",
        mode === 'preview' ? 'opacity-100' : 'opacity-0 hidden'
      )}>
        <div className="p-4 bg-white">
          {rawMode ? (
            <div className="border rounded overflow-hidden">
              <iframe
                title="Email HTML Preview"
                className="w-full h-[600px] bg-white"
                srcDoc={getPreviewContent()}
              />
            </div>
          ) : editor && (
            <div className="email-preview-container">
              <div className="email-header">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-sm text-gray-700">From: Your Company &lt;example@yourcompany.com&gt;</div>
                    <div className="text-sm text-gray-700 mt-1">To: <span className="font-medium">Test Customer &lt;customer@example.com&gt;</span></div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-700">Subject: <span className="font-medium">
                      {subject.replace(/\{\{(\w+)\}\}/g, (match, key) => {
                        const previewValue = {
                          firstName: "John",
                          customerName: "John Smith",
                          invoiceNumber: "INV-001",
                          companyName: "Ocean Cruises LLC",
                        }[key];
                        return previewValue || match;
                      })}
                    </span></div>
                  </div>
                </div>
              </div>
              <div className="email-body">
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="border-t p-4 flex justify-end gap-2">
          <Button 
            variant="outline"
            onClick={() => setTestEmailOpen(true)}
          >
            Send Test Email
          </Button>
          
          <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
            <DialogContent aria-describedby="test-email-description">
              <DialogHeader>
                <DialogTitle>Send Test Email</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div id="test-email-description" className="sr-only">
                  Enter recipient email for test message
                </div>
                <Input
                  type="email"
                  placeholder="Recipient email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setTestEmailOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={sendTestEmail}
                    disabled={isSending}
                  >
                    {isSending ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {previewContent}
      </div>
    </div>
  );
}
